'use client';

import { FC, useEffect, useState, useMemo } from 'react';
import Link from 'next/link';
import { Product, Variant } from 'shared/types/product';
import { ProductCardProduct } from 'shared/types/product/Product';
import { gtmViewItemList } from 'helpers/utils/gtm-events/gtmEccomerce';
import { LineItemMapper } from 'helpers/utils/gtm-events/mapper/LineItemMapper';
import { mapProductsToDisplayableVariants } from 'helpers/utils/product/productFilterUtils';
import { observeElementsInViewport } from 'helpers/utils/viewportObserver';
import { useCart, useAccount } from 'frontastic/hooks';
import ProductListCard from '../../product-list/components/ProductListCard';
import ProductQuickViewModal from '../../product-list/components/ProductQuickViewModal';

interface ProductRecommendationGridProps {
  /** Title displayed above the grid */
  title: string;
  /** Array of products to display */
  products: ProductCardProduct[];
  /** Location identifier for GTM tracking (e.g., "Shop Collection", "You May Also Like") */
  location: 'Recently Viewed' | 'You May Also Like' | 'Shop Collection';
  /** Optional link to view all products */
  viewAllLink?: string;
  /** Optional boolean to make section expandable to show all in grid */
  collapsable?: boolean;
  /** Optional function to determine preferred color for each product */
  colorMatcher?: (product: ProductCardProduct) => string | undefined;
  /** Maximum number of items to display (default: 4) */
  maxItems?: number;
  /** Optional HTML id for the section (used for viewport tracking) */
  sectionId?: string;
  /** Optional flag for shop collection specific behavior */
  isShopCollection?: boolean;
}

/**
 * Reusable product recommendation grid component
 *
 * Consolidates duplicate logic from:
 * - ProductShopCollection
 * - ProductsYouMayAlsoLike
 * - ProductRecentlyViewedSection
 *
 * Features:
 * - Filters out discontinued products without stock
 * - Handles color matching for shop collections
 * - GTM viewport tracking
 * - Quick view modal
 * - Responsive grid layout
 */
export const ProductRecommendationGrid: FC<ProductRecommendationGridProps> = ({
  title,
  products,
  location,
  viewAllLink,
  colorMatcher,
  maxItems = 4,
  sectionId,
  collapsable = false,
  isShopCollection = false,
}) => {
  const { data: cart } = useCart();
  const { account } = useAccount();
  const [observerInitialized, setObserverInitialized] = useState(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>(undefined);
  const [selectedProductVariant, setSelectedProductVariant] = useState<Variant | undefined>(undefined);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const allDisplayableProducts = useMemo(() => {
    return mapProductsToDisplayableVariants(products, colorMatcher);
  }, [products, colorMatcher]);

  const visibleProducts =
    collapsable && !isExpanded ? allDisplayableProducts.slice(0, maxItems) : allDisplayableProducts;

  // GTM viewport tracking
  useEffect(() => {
    if (allDisplayableProducts.length > 0 && !observerInitialized && sectionId) {
      setObserverInitialized(true);
      observeElementsInViewport({
        selector: `#${sectionId}`,
        callback: () => {
          gtmViewItemList({
            item_list_name: location,
            itemsDataLayer: allDisplayableProducts.map(({ product, displayVariant }) =>
              LineItemMapper.fromProductToItemDataLayer(product, 1, displayVariant),
            ),
            cart,
            account,
          });
        },
      });
    }
  }, [allDisplayableProducts, observerInitialized, sectionId, location, cart, account]);

  const selectProduct = (product: Product) => {
    setSelectedProduct(product);
  };

  const selectProductVariant = (variant: Variant | undefined) => {
    setSelectedProductVariant(variant);
  };

  const openModal = () => {
    setIsOpen(true);
  };

  if (allDisplayableProducts.length === 0) return null;

  return (
    <>
      {selectedProduct && isOpen && (
        <ProductQuickViewModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          product={selectedProduct}
          defaultVariant={selectedProductVariant}
        />
      )}

      <section id={sectionId}>
        <div>
          <div className="mb-11 flex items-center justify-between">
            <h3 className="text-[28px] font-semibold leading-[34px] text-[#222]">{title}</h3>
            {viewAllLink && (
              <Link aria-label="View All" href={viewAllLink} className="text-primary-gray hover:underline">
                View All
              </Link>
            )}
            {collapsable && products.length > maxItems && (
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex size-6 items-center justify-center rounded-full border-2 border-primary-dark text-2xl leading-none"
                aria-label={isExpanded ? 'Show less products' : 'Show all products'}
                aria-expanded={isExpanded}
              >
                {isExpanded ? '−' : '+'}
              </button>
            )}
          </div>
        </div>

        <ul className="mb-11 grid w-full grid-cols-2 gap-x-5 gap-y-[30px] md:gap-x-[27px] lg:grid-cols-4">
          {allDisplayableProducts.map(({ product, displayVariant, defaultColor }, index) => {
            const isVisible = index < visibleProducts.length;
            return (
              <li
                key={`${location.toLowerCase().replace(/\s+/g, '-')}-${displayVariant.sku}`}
                className={`transition-all duration-500 ease-in-out ${
                  isVisible
                    ? 'max-h-[1000px] scale-100 opacity-100'
                    : 'pointer-events-none max-h-0 scale-95 overflow-hidden opacity-0'
                }`}
              >
                <ProductListCard
                  location={location}
                  product={product}
                  selectProduct={selectProduct}
                  openModal={openModal}
                  defaultColor={defaultColor}
                  selectProductVariant={selectProductVariant}
                  isShopCollection={isShopCollection}
                />
              </li>
            );
          })}
        </ul>
      </section>
    </>
  );
};

export default ProductRecommendationGrid;
